import type { SegmentFilter } from '@workspace/types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type SegmentFieldsProps = {
  value: SegmentFilter;
  onChange: (next: SegmentFilter) => void;
};

function numberOrEmpty(value: number | undefined): string {
  return typeof value === 'number' ? String(value) : '';
}

function parseOptionalNumber(raw: string): number | undefined {
  const bounded = raw.slice(0, 16).trim();
  if (!bounded) {
    return undefined;
  }
  const parsed = Number(bounded);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

function parseOptionalInt(raw: string): number | undefined {
  const parsed = parseOptionalNumber(raw);
  if (parsed === undefined || !Number.isInteger(parsed)) {
    return undefined;
  }
  return Math.min(parsed, 9999);
}

export function ContactAgentSegmentFields({ value, onChange }: SegmentFieldsProps) {
  const patch = (partial: Partial<SegmentFilter>) => onChange({ ...value, ...partial });

  return (
    <div className='space-y-3'>
      <label className='flex items-center gap-2 text-sm'>
        <input
          type='checkbox'
          checked={value.overdueOnly !== false}
          onChange={event => patch({ overdueOnly: event.target.checked })}
        />
        Solo facturas vencidas
      </label>
      <div className='grid gap-3 sm:grid-cols-2'>
        <div className='space-y-1'>
          <Label htmlFor='minTotalBalance'>Monto desde</Label>
          <Input
            id='minTotalBalance'
            inputMode='decimal'
            value={numberOrEmpty(value.minTotalBalance ?? value.minBalance)}
            onChange={event => patch({ minTotalBalance: parseOptionalNumber(event.target.value) })}
            placeholder='Sin minimo'
          />
        </div>
        <div className='space-y-1'>
          <Label htmlFor='maxTotalBalance'>Monto hasta</Label>
          <Input
            id='maxTotalBalance'
            inputMode='decimal'
            value={numberOrEmpty(value.maxTotalBalance)}
            onChange={event => patch({ maxTotalBalance: parseOptionalNumber(event.target.value) })}
            placeholder='Sin maximo'
          />
        </div>
        <div className='space-y-1'>
          <Label htmlFor='minInvoiceCount'>Facturas desde</Label>
          <Input
            id='minInvoiceCount'
            inputMode='numeric'
            value={numberOrEmpty(value.minInvoiceCount)}
            onChange={event => patch({ minInvoiceCount: parseOptionalInt(event.target.value) })}
            placeholder='1'
          />
        </div>
        <div className='space-y-1'>
          <Label htmlFor='maxInvoiceCount'>Facturas hasta</Label>
          <Input
            id='maxInvoiceCount'
            inputMode='numeric'
            value={numberOrEmpty(value.maxInvoiceCount)}
            onChange={event => patch({ maxInvoiceCount: parseOptionalInt(event.target.value) })}
            placeholder='Sin maximo'
          />
        </div>
      </div>
      <p className='text-xs text-muted-foreground'>
        El monto y la cantidad se calculan por contacto sobre la ultima sync de este agente.
      </p>
    </div>
  );
}
